import { useCallback, useEffect, useState } from "react";
import type { FieldValues, UseFormReturn } from "react-hook-form";

/**
 * Manages a baseline snapshot for form-backed tables with per-cell dirty highlighting.
 *
 * **Why we need it:** DirtyAwareCell compares current values against a baseline to show
 * which cells changed. Query-derived defaultValues stay stale after save until refetch,
 * so we need a separate baseline: synced from defaultValues when data loads, updated to
 * saved values in updateBaseline. Without it, previously saved cells would incorrectly
 * stay highlighted after editing another cell.
 *
 * **Where to use it:** In tables with DirtyAwareCell. Pass the result to `tableFeatures.editionHighlight`:
 * `tableFeatures={{ editionHighlight: { baseline, recordKey? } } }`. recordKey defaults to "entries". ListContent injects
 * FormTableCellDirtyProvider automatically—no need to wrap manually. Call `updateBaseline`
 * after a successful mutation.
 */
export function useFormBaseline<TForm extends FieldValues>(
  form: UseFormReturn<TForm>,
  defaultValues: TForm,
): { baseline: TForm; updateBaseline: () => void } {
  const [baseline, setBaseline] = useState<TForm>(defaultValues);

  useEffect(() => {
    setBaseline(defaultValues);
  }, [defaultValues]);

  const updateBaseline = useCallback(() => {
    const values = form.getValues();
    form.reset(values);
    setBaseline(values);
  }, [form]);

  return { baseline, updateBaseline };
}
